import React, { useEffect, useState } from "react";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import { Alert, Tab, Tabs } from "@mui/material";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import FlowActionButton from "@components/FlowActionButtons/FlowActionButton";
import PrintButton from "@components/FlowActionButtons/PrintButton";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import { useBayHireDetailActions } from "@features/bay-hire/hooks/useBayHireDetailActions";
import { useBayHireRequestById } from "@features/bay-hire/hooks/useBayHireQueries";
import {
  getAttachmentFileName,
  useDownloadBayHireAttachment,
} from "@features/bay-hire/hooks/useDownloadBayHireAttachment";
import { findLatestAttachmentByType } from "@features/bay-hire/utils/bayHireAttachments";
import {
  canCancelBayHireRequest,
  canRenewBayHireRequest,
  canResubmitBayHireRequest,
  canUpdateBayHireRequest,
  canUploadBayHirePaymentReceipt,
} from "@features/bay-hire/utils/bayHireRequestActionGuards";
import { formatBayHireDate } from "@features/bay-hire/utils/formatBayHireDate";
import { getBayHireStatusBadgeType } from "@features/bay-hire/utils/statusBadge";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  BAY_HIRE_BFF_SERVICE_SEGMENT,
  getBayHirePrintDocument,
  type Attachment,
} from "@services/bayHireApi";
import { paths } from "@utils/navigation";
import { printRequestedTemplate } from "@utils/printRequestedTemplate";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import AddToWaitingListDialog from "../form/AddToWaitingListDialog";
import ApproveExemptedDialog from "../form/ApproveExemptedDialog";
import CancelDialog from "../form/CancelDialog";
import DetailsDialog from "../form/DetailsDialog";
import PreApproveDialog from "../form/PreApproveDialog";
import PreApprovedSummaryDialog from "../form/PreApprovedSummaryDialog";
import ReceiptUploadDialog from "../form/ReceiptUploadDialog";
import RejectDialog from "../form/RejectDialog";
import RejectPaymentDialog from "../form/RejectPaymentDialog";
import BayHireAttachmentsSection from "./BayHireAttachmentsSection";
import BayHireFileCard from "./BayHireFileCard";
import BayHireFlowButtons from "./BayHireFlowButtons";
import BayInformationCard from "./BayInformationCard";
import CompanyDetailsCard from "./CompanyDetailsCard";

// Bay Hire's cancel/resubmit/upload-receipt Vendor actions are async under
// the hood (BFF returns 202 and signals a Temporal workflow rather than
// updating the domain synchronously - see BayHireOperationsService's
// uploadBayHireReceipt/cancelViaVendorPath javadoc). A single immediate
// refetch() right after the mutation resolves races the workflow signal
// being processed and reliably shows stale (pre-mutation) status - observed
// live for receipt upload (PRE_APPROVED never visibly became PAYED without
// a manual page reload). Poll a few times with a short delay until the
// status actually changes, falling back to whatever the last refetch
// returned if the async effect hasn't landed within the budget (matches the
// backend smoke suite's own wait_for_status retry pattern).
const POLL_FOR_STATUS_CHANGE_ATTEMPTS = 5;
const POLL_FOR_STATUS_CHANGE_DELAY_MS = 1000;

const BayHireRequestDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { notify } = useNotification();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  // DFT-0027 (Slice B1): id now comes from the route (:id) instead of a prop.
  const { id: requestId } = useParams();
  const listPath = `/${paths.bayHire.index}`;
  // DFT-0073: this page OPTS IN to polling (see useBayHireRequestById - the other caller, the
  // edit form, must not), so an auto-unclaim by Task Management surfaces here - Claim reappearing
  // - without a manual reload. The poll stands down while any dialog is open; the hook's own
  // comment explains why that is required rather than merely tidy.
  //
  // A mirrored piece of STATE rather than the `openDrawers` value directly, because `openDrawers`
  // comes from useBayHireDetailActions below (it needs this query's `refetch`) and so does not
  // exist yet at this line. It must be state and not a ref: React Query re-arms a paused interval
  // only when the query re-renders, and a ref mutation re-renders nothing - the pause would stick
  // forever. Costs one extra render per dialog open/close.
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    data: request,
    isLoading,
    isError,
    refetch,
  } = useBayHireRequestById(requestId ?? null, undefined, { poll: !dialogOpen });
  // DFT-0027 (Slice B2): Operations users get the relocated lifecycle actions
  // (from the DFT-0034 queue kebab) as a header button row; vendors keep the
  // existing Cancel / Resubmit / Upload Receipt controls.
  const isOperationsUser = useAuthStore((s) => s.isOperationsUser) ?? false;
  const {
    inFlight,
    openDrawers,
    openDrawer,
    closeDrawer,
    handleClaim,
    handleUnclaim,
    handleVerifyPayment,
    // Arrow, not a direct reference: pollForStatusChange is declared below and
    // would be in its temporal dead zone at this point. It only runs on user
    // action, by which time both it and `request` are initialised.
  } = useBayHireDetailActions(requestId ?? undefined, refetch, () =>
    pollForStatusChange(request?.status ?? ""),
  );
  const anyDialogOpen = Object.values(openDrawers).some(Boolean);
  useEffect(() => {
    setDialogOpen(anyDialogOpen);
  }, [anyDialogOpen]);
  // DFT-0027, rewritten by DFT-0050 (AD1/AD3): attachments (and the receipt file) are fetched
  // directly from Azure via a SAS token, not mediated through any backend; failures surface as an
  // error toast, matching how useBayHireDetailActions reports its own failures.
  const { isBusy, downloadAttachment, openAttachment } = useDownloadBayHireAttachment();

  // FE-32: `Bay Hire Details` | `Documents` tabs. Local state, not router-driven like
  // BayHireOperationsTabs - these are two views of ONE request at one route, not two
  // independently deep-linkable pages, so there is nothing for a URL segment to distinguish.
  const [activeTab, setActiveTab] = useState<0 | 1>(0);

  const handleDownloadAttachment = (attachment: Attachment) => {
    if (!requestId) return;
    void downloadAttachment(
      requestId,
      attachment.id,
      attachment.documentUrl,
      getAttachmentFileName(attachment),
    );
  };

  // DFT-0050 (AC29): opens the attachment in a new tab, independent of downloading it to disk -
  // every attachment surface on this page gets both controls, not one doing both.
  const handleViewAttachment = (attachment: Attachment) => {
    if (!requestId) return;
    void openAttachment(requestId, attachment.id, attachment.documentUrl);
  };

  // DFT-0045: server-generated PDF, matching Gatepass/Security-Clearance's own
  // handlePrint call sites exactly - both reused unmodified.
  const handlePrint = async () => {
    await printRequestedTemplate({
      id: requestId,
      service: BAY_HIRE_BFF_SERVICE_SEGMENT,
      requestTemplateDocument: getBayHirePrintDocument,
      notify,
      t,
    });
  };

  const handleResubmit = () => {
    if (!request) return;
    navigate(`/${paths.bayHire.request}`, { state: { request } });
  };

  const handleUpdateRequest = () => {
    if (!request) return;
    navigate(`/${paths.bayHire.request}`, { state: { request } });
  };

  const handleRenewRequest = () => {
    if (!request) return;
    navigate(`/${paths.bayHire.request}`, { state: { request } });
  };

  /**
   * DFT-0044 bug fix (2026-08-03): this used to exhaust its attempts and return silently, which is
   * how a permanently-failed workflow looked identical to a slow one. An Ops decision that the
   * domain rejected (e.g. a blank Total Amount answering 400, which Temporal classifies as
   * non-retryable and which therefore FAILS the workflow) left the request at its old status with
   * no indication anything had gone wrong. It now reports whether the status actually moved so the
   * caller can say so.
   *
   * <p>It cannot distinguish "failed" from "still catching up" - only the absence of a change
   * within the budget - so the message is deliberately hedged rather than asserting a failure.
   *
   * <p>Bug fix (2026-08-04): add-to-waiting-list completes the current claimed task and the
   * workflow loops back to `createInitialOperationsTask` for a fresh, unclaimed one (see
   * BayHireWorkflowImpl's WAITING_LIST branch) - but that new task lands strictly AFTER the
   * domain status flips to WAITING_LIST. Stopping the poll on the status change alone (as before)
   * raced that task creation and froze the page on the OLD, now-completed task's
   * `currentTaskStatus` (neither READY nor CLAIMED), so BayHireFlowButtons rendered nothing until
   * a manual reload. `awaitTaskStatus` lets a caller also require `currentTaskStatus` to land on a
   * specific value before accepting the poll as settled; callers that don't pass it keep the old
   * status-only behaviour.
   */
  const pollForStatusChange = async (
    previousStatus: string,
    awaitTaskStatus?: string,
  ): Promise<boolean> => {
    for (let attempt = 0; attempt < POLL_FOR_STATUS_CHANGE_ATTEMPTS; attempt++) {
      const result = await refetch();
      const statusChanged = result.data?.status !== previousStatus;
      const taskStatusSettled =
        !awaitTaskStatus || result.data?.currentTaskStatus === awaitTaskStatus;
      if (statusChanged && taskStatusSettled) {
        return true;
      }
      if (attempt < POLL_FOR_STATUS_CHANGE_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, POLL_FOR_STATUS_CHANGE_DELAY_MS));
      }
    }
    return false;
  };

  /** Wraps the poll so every async Ops decision reports a non-change the same way. */
  const pollAndReportStatusChange = async (previousStatus: string, awaitTaskStatus?: string) => {
    const changed = await pollForStatusChange(previousStatus, awaitTaskStatus);
    if (!changed) {
      notify({
        title: t("bay-hire.status-unchanged-title"),
        description: t("bay-hire.status-unchanged-description"),
        type: "warning",
      });
    }
    return changed;
  };

  if (isLoading) {
    return (
      <div className="relative text-center py-8">
        <LoadingOverlay loading />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" data-testid="bay-hire-detail-error">
        {t("bay-hire.loading-request-details-error")}
      </Alert>
    );
  }

  if (!request) {
    return (
      <Alert severity="warning" data-testid="bay-hire-detail-error">
        {t("bay-hire.request-not-found")}
      </Alert>
    );
  }

  // "Back to List" must land on the list the user actually came from. A plain
  // /bay-hire link cannot: BayHireContainer redirects Operations to the Ongoing
  // tab, so leaving a waiting-list request dropped the user on Ongoing.
  // Derived from status rather than router state so it also holds when the
  // detail URL is opened directly (deep link, bookmark, notification), where no
  // referrer state exists. A request that leaves WAITING_LIST during the visit
  // (e.g. pre-approved here) then correctly returns to Ongoing.
  //
  // Megaplan (2026-08-05, Operations 3-tab restructure): a 3rd branch for the
  // resolved/terminal statuses (APPROVED/REJECTED/CANCELLED), which now live
  // in their own History tab rather than Ongoing - see
  // useBayHireOperationsQueue.tsx's STATUSES_BY_VIEW for the full partition.
  const isHistoryStatus =
    request.status === "APPROVED" ||
    request.status === "REJECTED" ||
    request.status === "CANCELLED";

  const backPath = !isOperationsUser
    ? listPath
    : request.status === "WAITING_LIST"
      ? `/${paths.bayHire.waitingList}`
      : isHistoryStatus
        ? `/${paths.bayHire.history}`
        : `/${paths.bayHire.operationsQueue}`;

  // DFT-0044 slice F (F2): extracted to bayHireRequestActionGuards so this header's own rules
  // and the vendor list's row kebab (BayHireRequestList) cannot drift apart.
  const canUpdateRequest = canUpdateBayHireRequest(request);
  // Resubmit sits with the other header actions rather than inside the rejection banner - see the
  // header row below. Vendor-only, like Cancel and Update Request.
  const canResubmit = !isOperationsUser && canResubmitBayHireRequest(request);
  // DFT-0016: same rationale as canUpdateRequest above - centralised in
  // bayHireRequestActionGuards so this header's rule and any future list-kebab entry
  // cannot drift apart.
  const canRenewRequest = canRenewBayHireRequest(request);
  // Bug fix (2026-08-04): uploading the payment receipt is exclusively a Vendor
  // (Shipping Agent) action - Operations only ever verifies/rejects it once uploaded
  // (BayHireFlowButtons' Verify Payment/Reject Payment). The guard itself stays
  // role-agnostic (shared with BayHireRequestList's vendor row kebab), so the
  // Operations exclusion is applied here, at the one call site Operations also renders.
  const canUploadReceipt = !isOperationsUser && canUploadBayHirePaymentReceipt(request);
  // Receipt only carries an attachmentId - the downloadable file is the
  // matching entry in request.attachments (no documentUrl on Receipt itself).
  const receiptAttachment = request.receipt
    ? (request.attachments?.find((a) => a.id === request.receipt?.attachmentId) ?? null)
    : null;
  // FE-33/FE-35: the HOD approval document gets its own named card in the Documents tab,
  // distinct from the generic vendor attachments list it used to sit in undifferentiated.
  //
  // DFT-0044 (Update Request fix): NOT `.find()` any more - Update Request now lets a vendor
  // re-upload this document (BayHireCreateAttachmentUpload.tsx), and the domain does not delete
  // the old one when a new one arrives (findLatestAttachmentByType's own note), so more than one
  // HOD_APPROVAL attachment can legitimately exist. `.find()` would have kept surfacing the
  // OLDEST one, silently hiding every re-upload.
  const hodApprovalAttachment = findLatestAttachmentByType(request.attachments, "HOD_APPROVAL");
  // DFT-0088 (AD1): reject()/cancel()/preApprove() (bay-hire-service) validate a submitted
  // SUPPORTING_DOCUMENT attachmentId but never durably record which one a completed action
  // actually used (StatusTransition has no attachmentId column) - so a discarded
  // remove-then-reupload attempt, or an earlier reject/resubmit cycle's own evidence, would
  // otherwise stay permanently visible as a "ghost" alongside the current one. Computed
  // unconditionally (not gated by status), mirroring HOD_APPROVAL's own findLatestAttachmentByType
  // treatment above - Pre-approve's own Attachments upload (AC2) has no REJECTED/CANCELLED
  // Details-drawer slot to dedupe through, so the generic Attachments list itself has to be the
  // one place this ever gets resolved for that surface.
  const latestSupportingDocumentAttachment = findLatestAttachmentByType(
    request.attachments,
    "SUPPORTING_DOCUMENT",
  );
  // DFT-0044 slice F (F4): the Details drawer only ever renders for a REJECTED/CANCELLED
  // request's own reason - only then does the latest SUPPORTING_DOCUMENT have a dedicated
  // reason-attachment slot to appear in instead of the generic list.
  const hasReasonAttachmentContext =
    request.status === "REJECTED" || request.status === "CANCELLED";
  // DFT-0104: the three statuses reachable only via Pre-approve, and the only statuses at which
  // a SUPPORTING_DOCUMENT attachment can exist at all (uploaded during the Pre-approve dialog
  // itself, per flows-bay-hire.md#L106-114) - REQUESTED/WAITING_LIST predate Pre-approve, so no
  // gating is needed there. Neither the wireframes/DPR nor the already-correct Cancel/Reject
  // pattern show a standalone Attachments card at these statuses; the file is relocated to the
  // "View details" drawer instead, extending PreApprovedSummaryDialog's PRE_APPROVED-only scope
  // to PAYED/APPROVED too (a presumption, flagged in the PR per the requester's 2026-09-02 scope
  // decision).
  const isPreApproveOrLaterStatus =
    request.status === "PRE_APPROVED" ||
    request.status === "PAYED" ||
    request.status === "APPROVED";
  // The receipt file, the HOD approval document, and (when shown via the Details/Pre-approved-
  // summary drawer) the current reason's or Pre-approve's own supporting document each get their
  // own card/dialog, so none is repeated in the general Attachments list. Every OLDER
  // (non-latest) SUPPORTING_DOCUMENT row is excluded outright regardless of status - this closes
  // AC2's Pre-approve duplication for the PRE_APPROVED/PAYED/APPROVED read side (DFT-0104), not
  // just the REJECTED/CANCELLED Details-drawer case. PAYMENT_RECEIPT is similarly excluded in
  // full unless it is the genuine, submitted receipt - unlike SUPPORTING_DOCUMENT, it already has
  // a scalar "genuine" reference (`receipt.attachmentId`), so an abandoned/removed re-upload
  // attempt from Add Receipt's own eager-upload-on-selection model (DL-017) has nothing
  // legitimate left to distinguish it from the real one. HOD_APPROVAL is excluded in full for the
  // same reason as SUPPORTING_DOCUMENT: the `a.id === hodApprovalAttachment?.id` check above only
  // catches the LATEST HOD_APPROVAL row - any older (superseded, e.g. from an Update Request
  // re-upload) HOD_APPROVAL attachment used to fall through to the final `return true` and leak
  // into the general Attachments card, which has no HOD_APPROVAL slot of its own to belong in.
  const otherAttachments = (request.attachments ?? []).filter((a) => {
    if (a.id === receiptAttachment?.id || a.id === hodApprovalAttachment?.id) return false;
    if (a.attachmentType === "SUPPORTING_DOCUMENT") {
      if (a.id !== latestSupportingDocumentAttachment?.id) return false;
      return !hasReasonAttachmentContext && !isPreApproveOrLaterStatus;
    }
    if (a.attachmentType === "PAYMENT_RECEIPT") return a.id === receiptAttachment?.id;
    if (a.attachmentType === "HOD_APPROVAL") return false;
    return true;
  });

  // DFT-0044 slice F (F4): the Details drawer's own reason + F3 supporting-document
  // attachment(s), keyed off the CURRENT status - a request can only be REJECTED or CANCELLED at
  // a time (both terminal-for-this-cycle), never both at once.
  const supportingDocumentAttachments =
    hasReasonAttachmentContext && latestSupportingDocumentAttachment
      ? [latestSupportingDocumentAttachment]
      : [];
  // DFT-0104: mirrors supportingDocumentAttachments above, for the PreApprovedSummaryDialog side
  // - the same shape (an array wrapping zero-or-one attachment) keeps both call sites
  // structurally consistent.
  const preApprovedSupportingDocumentAttachments =
    isPreApproveOrLaterStatus && latestSupportingDocumentAttachment
      ? [latestSupportingDocumentAttachment]
      : [];
  const detailsReason =
    request.status === "REJECTED"
      ? request.rejectionReason
      : request.status === "CANCELLED"
        ? request.cancellationReason
        : null;
  const detailsReasonLabel =
    request.status === "REJECTED"
      ? t("bay-hire.details-rejection-reason-label")
      : t("bay-hire.details-cancellation-reason-label");

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId="bay-hire-detail-subheader"
        subtitle={
          <div
            className="flex items-center space-x-2 text-sm text-gray-700"
            data-testid="bay-hire-detail-breadcrumb"
          >
            {/* `05_Port_License_details-15.png`: "Bay Hire / 123" - the breadcrumb's own link
                text is "Bay Hire" (not "Back to List"), and the trailing value is the human
                reference number, not the UUID this rendered before checking the wireframe. */}
            <Link
              to={backPath}
              className="text-blue-600 hover:underline"
              data-testid="bay-hire-detail-back-button"
            >
              {t("bay-hire.title")}
            </Link>
            <span className="text-gray-400">/</span>
            <span data-testid="bay-hire-detail-breadcrumb-current">
              {request.requestNo ?? request.id}
            </span>
          </div>
        }
        title={
          <div className="flex items-center justify-between w-full">
            {/* `05_Port_License_details-15.png`: the heading IS the reference number ("123"),
                not the literal text "Request Details" this rendered before checking the
                wireframe - falls back to the id only for the (should not happen for a real
                request) case where requestNo is somehow absent. */}
            <div data-testid="bay-hire-detail-title">{request.requestNo ?? request.id}</div>
            {/* `05_Port_License_details-15.png`: ONLY buttons sit on this row, at the title's own
                level - status moves to its own row below (see `children`, right after this
                Subheader). It previously shared this row with the status chip, both squeezed
                against the buttons. DFT-0027 (Slice B1/B2): Operations users get the relocated
                lifecycle actions (BayHireFlowButtons); vendors keep the Cancel Request control. */}
            <div className="flex items-center gap-2 text-sm font-normal">
              {/* DFT-0045: one shared PrintButton before the persona branch - every wireframe
                  screen shows Print leftmost for both Ops and the Vendor at every status
                  (05_Port_License_details*.png), so a single call site here avoids duplicating
                  the button + its wiring inside both BayHireFlowButtons (Ops) and the Vendor
                  branch below. No canPrintBayHireRequest guard - the page's own access control
                  (getBayHireRequest 404s an unauthorized caller before this page ever renders)
                  is what already prevents an unauthorized print, matching every other print
                  button in the codebase (PrintLicense, Gatepass, Security Clearance). */}
              <PrintButton
                langBase="bay-hire"
                permission=""
                isActionAvailable
                onPrint={handlePrint}
              />
              {isOperationsUser ? (
                <BayHireFlowButtons
                  request={request}
                  isOperationsUser={isOperationsUser}
                  inFlight={inFlight}
                  onClaim={() => void handleClaim()}
                  onUnclaim={() => void handleUnclaim()}
                  onVerifyPayment={() => void handleVerifyPayment()}
                  openDrawer={openDrawer}
                />
              ) : (
                <>
                  {/* `04_Port_License_details-3.png`/`05_Port_License_details.png` (F1): Cancel
                      Request sits BEFORE Update Request, both Vendor-only - re-checked against
                      `05_Port_License_details.png` during the order/styling review: that screen's
                      own button row reads Print, Cancel Request, Update Request (Print is DFT-0045,
                      out of scope here). Neither button carries a semantic color there either
                      (plain blue outline, same as the Ops row's own Reject/Pre-approve - see
                      BayHireFlowButtons.tsx's own note) - `color="error"` on Cancel Request was a
                      Bay Hire-only divergence, not a design choice to preserve.

                      DFT-0044 (size fix): NOT `size="small"` either, for the same reason -
                      Gatepass/Movement's own header action buttons (FlowActionButton) never set a
                      `size` prop (MUI default, medium), so `size="small"` here was, again, a Bay
                      Hire-only divergence rather than a deliberate choice. */}
                  {/* DFT-0100: Cancel/Resubmit/Update now render via the shared FlowActionButton
                      (see BayHireFlowButtons.tsx's own note - same rationale: bay-hire's own
                      un-suffixed keys, not the named wrappers' hardcoded ones). Renew (below) is
                      unaffected - out of this task's scope. */}
                  <FlowActionButton
                    langBase="bay-hire"
                    isActionAvailable={canCancelBayHireRequest(request)}
                    labelKey="cancel-request"
                    testId="bay-hire-detail-cancel-button"
                    variant="outlined"
                    startIcon={<CancelOutlinedIcon />}
                    onClick={() => openDrawer("cancel")}
                  />
                  <FlowActionButton
                    langBase="bay-hire"
                    isActionAvailable={canResubmit}
                    labelKey="resubmit"
                    testId="bay-hire-detail-resubmit-button"
                    variant="outlined"
                    startIcon={<SyncIcon />}
                    onClick={handleResubmit}
                  />
                  <FlowActionButton
                    langBase="bay-hire"
                    isActionAvailable={canUpdateRequest}
                    labelKey="update-request"
                    testId="bay-hire-detail-update-button"
                    variant="outlined"
                    startIcon={<AutorenewIcon />}
                    onClick={handleUpdateRequest}
                  />
                  {/* DFT-0016 (DFE-BAY-HIRE-004): new UI - no existing button to reuse (verified
                      2026-08-04, zero matches for "renew" anywhere in this codebase). Placed after
                      Update Request; the wireframes never show it alongside Cancel/Update (it only
                      ever appears at APPROVED, where neither of those is available), so there is no
                      design precedent constraining its position relative to them. */}
                  {canRenewRequest && (
                    <Button
                      type="button"
                      onClick={handleRenewRequest}
                      testId="bay-hire-detail-renew-button"
                      variant="outlined"
                      label={
                        <span className="normal-case font-medium">
                          {t("bay-hire.renew-request")}
                        </span>
                      }
                    />
                  )}
                </>
              )}
            </div>
          </div>
        }
      >
        {/* `05_Port_License_details-15.png`: a colored dot + the status label, LEFT-aligned
            directly under the title - not an MUI Chip pill sharing the title's own row with the
            buttons, which is what rendered before checking the wireframe.

            The wireframe shows a trailing "- View details" link here for EVERY status.
            DFT-0044 slice F: built for REJECTED/CANCELLED (F4 - a read-only reason, already
            visible in the banner below, plus any F3 supporting-document attachment) AND now
            PRE_APPROVED (FE-38 - a read-only Chargeable/Total Amount/HOD document/Remarks summary,
            still a PRESUMPTION per FE-38's own note: no wireframe in either drop shows this read-
            only view, only the editable Pre-approve form that produces it - flag in the PR for
            design review). Every other status's own link stays omitted, per the earlier documented
            decision, until its own drawer content is similarly unambiguous or confirmed. */}
        <div className="flex items-center gap-1.5 mt-1" data-testid="bay-hire-detail-status-row">
          {/* DFT-0098: was a hand-rolled dot + span pair with its own getStatusChipColor/
              getStatusDotColor mapping, which disagreed with getBayHireStatusBadgeType (the list/
              queue grid's own mapping) for REQUESTED/CANCELLED. Now the same shared Badge
              component + single status->color mapping every other bay-hire status column uses. */}
          <Badge
            testId="bay-hire-detail-status-badge"
            type={getBayHireStatusBadgeType(request.status)}
            label={t(`bay-hire.status.${request.status}`, { defaultValue: request.status })}
          />
          {(request.status === "REJECTED" ||
            request.status === "CANCELLED" ||
            // DFT-0044 (FE-38, a presumption - flag in the PR for design review): opens a
            // read-only summary of the pre-approval decision rather than the Details drawer.
            // DFT-0104: extended from PRE_APPROVED-only to also cover PAYED/APPROVED, per the
            // requester's 2026-09-02 scope decision (also a presumption, flagged in the PR).
            isPreApproveOrLaterStatus) && (
            <>
              <span className="text-sm text-gray-700">-</span>
              <button
                type="button"
                onClick={() =>
                  openDrawer(isPreApproveOrLaterStatus ? "preApprovedSummary" : "details")
                }
                data-testid="bay-hire-detail-view-details-link"
                className="text-sm text-blue-600 hover:underline"
              >
                {t("bay-hire.view-details")}
              </button>
            </>
          )}
        </div>
      </Subheader>
      <div data-testid="bay-hire-detail" className="mt-6 p-8 px-24">
        {request.status === "REJECTED" && (
          <div
            role="alert"
            data-testid="bay-hire-detail-rejection-banner"
            className="mb-6 border border-red-300 bg-red-50 rounded-md p-4"
          >
            <p className="text-sm font-medium text-red-800">{t("bay-hire.request-rejected")}</p>
            <p className="text-sm text-red-700 mt-1">
              {request.rejectionReason || t("bay-hire.no-reason-provided")}
            </p>
            {/* Resubmit used to render HERE, inside the banner, below the reason. It moved to the
                header action row (above) so it sits with Print / Cancel request / Update request -
                every other action on this page. The banner keeps what a banner is for: telling the
                user what happened and why.

                No wireframe was ever produced for this screen: WG-2 in wireframes-bay-hire.md logs
                "Rejection reason display in Company request view + Resubmit button" as an open gap
                to be "addressed during implementation epics", which is why the original placement
                was an inline judgement call rather than a spec deviation. The IA lists Resubmit as
                a peer of the other detail actions ("[Rejected] -> View rejection reason |
                Resubmit", #L118), which is what settles it.

                Megaplan (2026-08-05) note, still true and carried over: Resubmit is exclusively a
                Shipping Agent capability - it hands back to the vendor's own list route, which an
                Operations session never renders, so the button was dead for Ops even when shown.
                It is now guarded by canResubmitBayHireRequest plus the same !isOperationsUser
                check every sibling vendor action uses. */}
          </div>
        )}

        {request.status === "WAITING_LIST" && (
          <output
            data-testid="bay-hire-detail-waiting-list-banner"
            className="block mb-6 border border-amber-300 bg-amber-50 rounded-md p-4"
          >
            <p className="text-sm font-medium text-amber-800">
              {t("bay-hire.request-waiting-list")}
            </p>
            <p className="text-sm text-amber-700 mt-1">{t("bay-hire.waiting-list-description")}</p>
          </output>
        )}

        {/* DFT-0044 FE-45/FE-54: the vendor's receipt call-to-action banner
            (05_Port_License_details-15.png). The mockup's copy reads "We need you receipt
            information" - a copy defect corrected to "your" per Q19, which says fix the copy and
            report the defect rather than reproduce it. The CTA is labelled `Add receipt` to match
            the baseline; the drawer it opens keeps its own title. */}
        {canUploadReceipt && (
          <output
            data-testid="bay-hire-detail-receipt-banner"
            className="mb-6 border border-blue-300 bg-blue-50 rounded-md p-4 flex items-center justify-between gap-4"
          >
            <div>
              <p className="text-sm font-medium text-blue-900">
                {t("bay-hire.receipt-banner-title")}
              </p>
              <p className="text-sm text-blue-800 mt-1">
                {t("bay-hire.receipt-banner-description")}
              </p>
            </div>
            <Button
              type="button"
              onClick={() => openDrawer("receiptUpload")}
              testId="bay-hire-detail-receipt-banner-add-receipt-button"
              variant="contained"
              size="small"
              label={
                <span className="normal-case font-medium">{t("bay-hire.add-receipt-action")}</span>
              }
            />
          </output>
        )}

        {request.status === "CANCELLED" && request.cancellationReason && (
          <div
            role="alert"
            data-testid="bay-hire-detail-cancellation-banner"
            className="mb-6 border border-amber-300 bg-amber-50 rounded-md p-4"
          >
            <p className="text-sm font-medium text-amber-800">{t("bay-hire.request-cancelled")}</p>
            <p className="text-sm text-amber-700 mt-1">{request.cancellationReason}</p>
          </div>
        )}

        {/* FE-32: `Bay Hire Details` | `Documents` tabs, replacing one flat scroll. Banners above
            stay OUTSIDE both tabs - a rejection/cancellation/waiting-list notice is status
            information the vendor needs regardless of which tab they are looking at, not
            "details" specifically. Mirrors BayHireOperationsTabs' MUI Tabs styling. */}
        <Tabs
          value={activeTab}
          onChange={(_, index: 0 | 1) => setActiveTab(index)}
          aria-label={t("bay-hire.detail.tabs-label")}
          data-testid="bay-hire-detail-tabs"
          sx={{
            "borderBottom": "1px solid var(--stroke-secondary-stroke, #E6EDF2)",
            "minHeight": 40,
            "mb": 3,
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "1rem",
              minHeight: 40,
              minWidth: "auto",
              px: 2,
            },
          }}
        >
          <Tab label={t("bay-hire.detail.detailsTab")} data-testid="bay-hire-detail-tab-details" />
          <Tab
            label={t("bay-hire.detail.documentsTab")}
            data-testid="bay-hire-detail-tab-documents"
          />
        </Tabs>

        {activeTab === 0 && (
          <div data-testid="bay-hire-detail-tab-panel-details">
            {/* DFT-0027: Figma p10 body - "Company details" card, then "Bay Information".
                The card only renders when the BFF managed to resolve the company from the
                request's companyId; there is no placeholder when it did not. */}
            {/* FE-41 moved the responsible person into this card. Those two values come from the
                REQUEST, not from the resolved company, so the card must also render when the BFF
                could not resolve `company` - otherwise moving them would make them vanish
                entirely on that path. DFT-0027's "no placeholder company" intent is preserved:
                unresolved company fields still render as `-` rather than being invented. */}
            {(request.company ||
              request.responsiblePersonName ||
              request.responsiblePersonDesignation) && (
              <CompanyDetailsCard
                company={request.company ?? ({} as NonNullable<typeof request.company>)}
                responsiblePersonName={request.responsiblePersonName}
                responsiblePersonDesignation={request.responsiblePersonDesignation}
              />
            )}

            <BayInformationCard request={request} />
          </div>
        )}

        {activeTab === 1 && (
          <div data-testid="bay-hire-detail-tab-panel-documents">
            {/* `05_Port_License_details-3.png`: `Head of department approval` and `Receipt` sit
                SIDE BY SIDE as two columns of one row, not stacked - corrected after checking the
                wireframe against what had been built without it. Vendor attachments (FE-35) render
                below, full width - the wireframe's own example has none to show a third column
                for, so there is no evidence for how one would sit alongside these two. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* FE-33: the HOD approval document, in its own named card - previously an
                  undifferentiated entry in the generic Attachments list, indistinguishable from
                  any other upload despite being the one document type the domain itself
                  validates. */}
              {hodApprovalAttachment && (
                <section
                  className="border border-gray-200 rounded-md bg-white p-6"
                  data-testid="bay-hire-detail-hod-approval-section"
                >
                  <h3 className="text-base font-semibold text-gray-900 mb-3">
                    {t("bay-hire.detail.hodApproval")}
                  </h3>
                  <BayHireFileCard
                    fileName={getAttachmentFileName(hodApprovalAttachment)}
                    caption={hodApprovalAttachment.attachmentType}
                    isDownloading={isBusy(hodApprovalAttachment.documentUrl)}
                    onView={() => handleViewAttachment(hodApprovalAttachment)}
                    onDownload={() => handleDownloadAttachment(hodApprovalAttachment)}
                    testId="bay-hire-detail-hod-approval-file"
                  />
                </section>
              )}

              {/* DFT-0014 (AD-13, AD-17): hidden entirely for EXEMPTED requests -
                  they skip PRE_APPROVED -> PAYED via the synchronous exempted-bypass,
                  so there is no payment section to show. */}
              {request.paymentType !== "EXEMPTED" && (request.receipt || canUploadReceipt) && (
                <section
                  className="border border-gray-200 rounded-md bg-white p-6"
                  data-testid="bay-hire-detail-payment-section"
                >
                  {/* FE-34: titled "Receipt", not "Payment Receipt" - the button below stays
                      "Upload Payment Receipt" (a separate, already-reviewed baseline mapping),
                      only this card's own heading changes. */}
                  <h3 className="text-base font-semibold text-gray-900 mb-3">
                    {t("bay-hire.detail.receipt")}
                  </h3>

                  {request.receipt && (
                    <div data-testid="bay-hire-detail-receipt">
                      {/* `05_Port_License_details-3.png`: the file link comes FIRST, then the
                          three metadata lines underneath as plain "label: value" text - not a
                          label/value grid ABOVE the file, which is what this rendered before
                          checking the wireframe. Receipt only carries an attachmentId (no
                          documentUrl), so the file is only downloadable when the matching
                          attachment is present in request.attachments; otherwise the metadata
                          stands alone. */}
                      {receiptAttachment && (
                        <BayHireFileCard
                          fileName={getAttachmentFileName(receiptAttachment)}
                          caption={receiptAttachment.attachmentType}
                          isDownloading={isBusy(receiptAttachment.documentUrl)}
                          onView={() => handleViewAttachment(receiptAttachment)}
                          onDownload={() => handleDownloadAttachment(receiptAttachment)}
                          testId="bay-hire-detail-receipt-file"
                        />
                      )}
                      <div className={receiptAttachment ? "mt-3 space-y-1" : "space-y-1"}>
                        <p className="text-sm text-gray-700">
                          {t("bay-hire.detail.receiptNumber")}: {request.receipt.receiptNumber}
                        </p>
                        <p className="text-sm text-gray-700">
                          {t("bay-hire.detail.receiptAmount")}: {request.receipt.receiptAmount}
                        </p>
                        <p className="text-sm text-gray-700">
                          {t("bay-hire.detail.receiptDate")}:{" "}
                          {formatBayHireDate(request.receipt.receiptDate)}
                        </p>
                      </div>
                    </div>
                  )}

                  {canUploadReceipt && (
                    <Button
                      type="button"
                      onClick={() => openDrawer("receiptUpload")}
                      testId="bay-hire-detail-upload-receipt-button"
                      variant="contained"
                      size="small"
                      label={
                        <span className="normal-case font-medium">
                          {t("bay-hire.add-receipt-action")}
                        </span>
                      }
                    />
                  )}
                </section>
              )}
            </div>

            {/* FE-35: vendor-uploaded attachments as their own third card group - a presumption
                (Q11.4), not a certain baseline requirement; flagged as such in the PR. */}
            {otherAttachments.length > 0 && (
              <BayHireAttachmentsSection
                attachments={otherAttachments}
                isDownloading={isBusy}
                onView={handleViewAttachment}
                onDownload={handleDownloadAttachment}
              />
            )}
          </div>
        )}

        {/* DFT-0044 (FE-55 area, AC39 decision): Cancel is exclusively a vendor (Shipping Agent)
            action - Operations has no Cancel button any more (BayHireFlowButtons.tsx), so this
            drawer is only ever opened from the vendor "Cancel Request" control above. */}
        {openDrawers.cancel && (
          <CancelDialog
            open
            requestId={request.id}
            onClose={() => closeDrawer("cancel")}
            onSuccess={async () => {
              closeDrawer("cancel");
              // DFT-0066 (AD-6): the Vendor cancel is now a Temporal Update, not a signal, so a
              // 2xx here means the workflow has ALREADY applied the cancellation - the domain was
              // written before the response was sent. A single refetch is therefore correct and
              // authoritative; there is nothing left to race.
              //
              // Replaces DFT-0038's poll, which existed because the BFF returned 202 the moment
              // the signal was accepted, so the status genuinely was not settled yet. A failure
              // now arrives as a 4xx and is surfaced by CancelDialog itself, rather than showing
              // up here as "status has not changed yet" - which was the only signal the user ever
              // got that a cancel had silently done nothing.
              //
              // pollAndReportStatusChange is deliberately RETAINED (AC21) for the Ops decision
              // paths below: those still go through temporal-signal-service, which is signal-only
              // and shared platform-wide, so they remain genuinely asynchronous.
              await refetch();
            }}
          />
        )}
        {openDrawers.receiptUpload && (
          <ReceiptUploadDialog
            open
            requestId={request.id}
            onClose={() => closeDrawer("receiptUpload")}
            onSuccess={async () => {
              closeDrawer("receiptUpload");
              // DFT-0038: receipt upload is async (BFF 202 + Temporal signal);
              // poll for the real status instead of a single racing refetch.
              await pollAndReportStatusChange(request.status);
            }}
          />
        )}

        {/* DFT-0027 (Slice B2/C): Operations-only lifecycle panels. Their shells
            are now the shared right-anchored SideDrawer (Slice C).
            Each polls on success rather than refetching once: these actions are
            async in the same way the Vendor ones DFT-0038 fixed are. The BFF
            completes a Task Management task, whose callback signals the workflow,
            which persists the new status in the domain afterwards - see
            BayHireOperationsService#preApprove ("this method does not call the
            domain directly"). A single refetch races that and leaves the page on
            the pre-action status. */}
        {isOperationsUser && openDrawers.preApprove && (
          <PreApproveDialog
            open
            requestId={request.id}
            numberOfBays={request.numberOfBays}
            dateFrom={request.dateFrom}
            dateTo={request.dateTo}
            dateFromApproved={request.dateFromApproved}
            dateToApproved={request.dateToApproved}
            // Drives the chargeable Total Amount rule - a blank amount on a chargeable request
            // used to reach the domain as 0 and kill the workflow permanently.
            paymentType={request.paymentType}
            onClose={() => closeDrawer("preApprove")}
            onSuccess={async () => {
              closeDrawer("preApprove");
              await pollAndReportStatusChange(request.status);
            }}
          />
        )}
        {/* DFT-0084: EXEMPTED-only direct approve - same async/eventually-consistent snapshot
            caveat as preApprove above (BayHireOperationsService#approveExempted mirrors
            preApprove exactly: completes the TM task, the domain effects land afterwards via
            the signal path). */}
        {isOperationsUser && openDrawers.approveExempted && (
          <ApproveExemptedDialog
            open
            requestId={request.id}
            dateFrom={request.dateFrom}
            dateTo={request.dateTo}
            onClose={() => closeDrawer("approveExempted")}
            onSuccess={async () => {
              closeDrawer("approveExempted");
              await pollAndReportStatusChange(request.status);
            }}
          />
        )}
        {isOperationsUser && openDrawers.reject && (
          <RejectDialog
            open
            requestId={request.id}
            onClose={() => closeDrawer("reject")}
            onSuccess={async () => {
              closeDrawer("reject");
              await pollAndReportStatusChange(request.status);
            }}
          />
        )}
        {isOperationsUser && openDrawers.addToWaitingList && (
          <AddToWaitingListDialog
            open
            requestId={request.id}
            requestNo={request.requestNo}
            onClose={() => closeDrawer("addToWaitingList")}
            onSuccess={async () => {
              closeDrawer("addToWaitingList");
              // Also wait for the fresh, unclaimed task the workflow creates on this
              // transition - see pollForStatusChange's bug-fix note above.
              await pollAndReportStatusChange(request.status, "READY");
            }}
          />
        )}
        {isOperationsUser && openDrawers.rejectPayment && (
          <RejectPaymentDialog
            open
            requestId={request.id}
            onClose={() => closeDrawer("rejectPayment")}
            onSuccess={async () => {
              closeDrawer("rejectPayment");
              await pollAndReportStatusChange(request.status);
            }}
          />
        )}
        {openDrawers.details && (
          <DetailsDialog
            open
            reasonLabel={detailsReasonLabel}
            reason={detailsReason}
            attachments={supportingDocumentAttachments}
            isDownloading={isBusy}
            onView={handleViewAttachment}
            onDownload={handleDownloadAttachment}
            onClose={() => closeDrawer("details")}
          />
        )}
        {openDrawers.preApprovedSummary && (
          <PreApprovedSummaryDialog
            open
            chargeable={request.chargeable}
            totalAmount={request.totalAmount}
            preApprovalRemarks={request.preApprovalRemarks}
            hodApprovalAttachment={hodApprovalAttachment}
            attachments={preApprovedSupportingDocumentAttachments}
            isDownloading={isBusy}
            onView={handleViewAttachment}
            onDownload={handleDownloadAttachment}
            onClose={() => closeDrawer("preApprovedSummary")}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default BayHireRequestDetail;
